import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  Skeleton,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CloudUpload as UploadIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  ImageNotSupported as NoImageIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { PageHeader } from '../../components/common';
import { adminApi } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useImageStreaming } from '../../hooks/useImageStreaming';

const RecipeDetail = () => {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fileInputRef = useRef(null);
  const { session } = useAuth();
  
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  
  // Streaming image generation hook
  const {
    isStreaming: generatingAIImage,
    streamStatus,
    partialImage,
    partialProgress,
    error: streamingError,
    finalImageUrl,
    startStreaming,
    cancelStreaming,
    reset: resetStreaming,
  } = useImageStreaming();

  useEffect(() => {
    fetchRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.getRecipe(recipeId);
      setRecipe(data);
    } catch (err) {
      console.error('Failed to fetch recipe:', err);
      setRecipe(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adminApi.deleteRecipe(recipeId);
      navigate('/recipes');
    } catch (err) {
      console.error('Failed to delete recipe:', err);
    }
  };

  const handleStartEdit = () => {
    setEditData({
      title: recipe?.title || '',
      description: recipe?.description || '',
      time: recipe?.time || '',
      inactive_time: recipe?.inactive_time || '',
      yield: recipe?.yield || '',
      ingredients: recipe?.ingredients || [],
      instructions: recipe?.instructions || [],
    });
    setEditMode(true);
    setError(null);
    setImagePreview(null);
    setPendingImageFile(null);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditData({});
    setError(null);
    setImagePreview(null);
    setPendingImageFile(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Upload pending image first if there is one
      if (pendingImageFile) {
        await adminApi.uploadRecipeImage(recipeId, pendingImageFile);
      }
      
      const updatePayload = {
        title: editData.title,
        description: editData.description,
        time: editData.time ? parseInt(editData.time, 10) : null,
        inactive_time: editData.inactive_time ? parseInt(editData.inactive_time, 10) : null,
        yield: editData.yield,
        ingredients: editData.ingredients,
        instructions: editData.instructions,
      };

      await adminApi.updateRecipe(recipeId, updatePayload);
      await fetchRecipe();
      setEditMode(false);
      setEditData({});
      setImagePreview(null);
      setPendingImageFile(null);
    } catch (err) {
      console.error('Failed to save recipe:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Image handling
  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Only JPEG, PNG, WebP, and GIF are allowed.');
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File too large. Maximum size is 10MB.');
        return;
      }
      
      setPendingImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = async () => {
    try {
      setImageUploading(true);
      await adminApi.deleteRecipeImage(recipeId);
      await fetchRecipe();
      setImagePreview(null);
      setPendingImageFile(null);
    } catch (err) {
      console.error('Failed to delete image:', err);
      setError('Failed to delete image. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleClearPendingImage = () => {
    setImagePreview(null);
    setPendingImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // AI Image generation with streaming
  const handleGenerateAIImage = async () => {
    try {
      setError(null);
      const authToken = session?.access_token;
      
      if (!authToken) {
        setError('Authentication required to generate image');
        return;
      }
      
      const imageUrl = await startStreaming(recipeId, authToken);
      
      if (imageUrl) {
        // Refresh recipe data to get the new image URL
        await fetchRecipe();
        setImagePreview(null);
        setPendingImageFile(null);
      }
    } catch (err) {
      console.error('Failed to generate AI image:', err);
      setError('Failed to generate AI image. Please try again.');
    }
  };
  
  // Handle streaming errors
  useEffect(() => {
    if (streamingError) {
      setError(`Image generation failed: ${streamingError}`);
    }
  }, [streamingError]);
  
  // When final image URL is received, refresh recipe
  useEffect(() => {
    if (finalImageUrl) {
      fetchRecipe();
      resetStreaming();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalImageUrl]);

  // Check if image is AI-generated based on URL pattern
  const isAIGeneratedImage = (url) => {
    return url && (url.includes('/ai-') || url.includes('recipe-images/ai-'));
  };

  // Ingredient handling
  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...editData.ingredients];
    if (field === 'name') {
      newIngredients[index] = { ...newIngredients[index], name: value };
    } else if (field === 'quantity') {
      newIngredients[index] = { 
        ...newIngredients[index], 
        quantity: { ...newIngredients[index]?.quantity, value: value } 
      };
    } else if (field === 'unit') {
      newIngredients[index] = { 
        ...newIngredients[index], 
        quantity: { ...newIngredients[index]?.quantity, unit: value } 
      };
    } else if (field === 'preparation') {
      newIngredients[index] = { ...newIngredients[index], preparation: value };
    }
    setEditData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const handleAddIngredient = () => {
    setEditData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', quantity: { value: '', unit: '' }, preparation: '' }]
    }));
  };

  const handleRemoveIngredient = (index) => {
    setEditData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  // Instruction handling
  const handleInstructionChange = (index, value) => {
    const newInstructions = [...editData.instructions];
    newInstructions[index] = value;
    setEditData(prev => ({ ...prev, instructions: newInstructions }));
  };

  const handleAddInstruction = () => {
    setEditData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const handleRemoveInstruction = (index) => {
    setEditData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const handleMoveInstruction = (index, direction) => {
    const newInstructions = [...editData.instructions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Bounds check
    if (newIndex < 0 || newIndex >= newInstructions.length) return;
    
    // Swap
    [newInstructions[index], newInstructions[newIndex]] = [newInstructions[newIndex], newInstructions[index]];
    setEditData(prev => ({ ...prev, instructions: newInstructions }));
  };

  if (loading) {
    return (
      <Box>
        <Skeleton width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      fontFamily: "'Space Mono', monospace",
      fontSize: '0.85rem',
    },
    '& .MuiInputLabel-root': {
      fontFamily: "'Space Mono', monospace",
      fontSize: '0.75rem',
    },
  };

  // Show partial image during streaming, otherwise show preview or saved image
  const currentImageUrl = partialImage || imagePreview || recipe?.image_url;

  return (
    <Box>
      <PageHeader
        title={recipe?.title?.toLowerCase() || 'recipe details'}
        breadcrumbs={[{ label: 'recipes', path: '/recipes' }, { label: recipe?.title?.toLowerCase() || 'recipe' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/recipes')}
            >
              Back
            </Button>
            {editMode ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleStartEdit}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Info */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              {/* Header with Image */}
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                {/* Image Section */}
                <Box sx={{ position: 'relative' }}>
                  {currentImageUrl ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={currentImageUrl}
                        sx={{
                          width: 200,
                          height: 150,
                          borderRadius: 2,
                          objectFit: 'cover',
                        }}
                      />
                      {/* AI Generated Badge */}
                      {isAIGeneratedImage(recipe?.image_url) && !imagePreview && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: '0.7rem',
                          }}
                        >
                          <AIIcon sx={{ fontSize: '0.875rem' }} />
                          AI
                        </Box>
                      )}
                      {editMode && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              opacity: 1,
                            },
                          }}
                        >
                          {(imageUploading || generatingAIImage) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 1 }}>
                              {generatingAIImage && partialProgress.total > 0 ? (
                                <>
                                  <Box sx={{ width: '80%' }}>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={(partialProgress.current / partialProgress.total) * 100}
                                      sx={{ 
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#fff' }
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#fff', textAlign: 'center' }}>
                                    {streamStatus || `Generating (${partialProgress.current}/${partialProgress.total})`}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                                  <Typography variant="caption" sx={{ color: '#fff' }}>
                                    {streamStatus || 'Starting...'}
                                  </Typography>
                                </>
                              )}
                              {generatingAIImage && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={cancelStreaming}
                                  sx={{ fontSize: '0.65rem', color: '#fff', borderColor: '#fff', mt: 0.5 }}
                                >
                                  Cancel
                                </Button>
                              )}
                            </Box>
                          ) : (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Replace
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<AIIcon />}
                                onClick={handleGenerateAIImage}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                AI Generate
                              </Button>
                              {pendingImageFile ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={handleClearPendingImage}
                                  sx={{ fontSize: '0.7rem', color: '#fff', borderColor: '#fff' }}
                                >
                                  Clear
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={handleDeleteImage}
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  Delete
                                </Button>
                              )}
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : editMode ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {generatingAIImage ? (
                        <Box
                          sx={{
                            width: 200,
                            height: 150,
                            borderRadius: 2,
                            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Show partial image as background if available */}
                          {partialImage && (
                            <Box
                              component="img"
                              src={partialImage}
                              sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                opacity: 0.7,
                              }}
                            />
                          )}
                          <Box sx={{ 
                            position: 'relative', 
                            zIndex: 1, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            backgroundColor: partialImage ? 'rgba(0,0,0,0.5)' : 'transparent',
                            padding: 2,
                            borderRadius: 1,
                          }}>
                            {partialProgress.total > 0 ? (
                              <>
                                <Box sx={{ width: 100, mb: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={(partialProgress.current / partialProgress.total) * 100}
                                  />
                                </Box>
                                <Typography variant="caption" sx={{ color: partialImage ? '#fff' : theme.palette.text.secondary }}>
                                  {streamStatus || `${partialProgress.current}/${partialProgress.total}`}
                                </Typography>
                              </>
                            ) : (
                              <>
                                <CircularProgress size={24} />
                                <Typography variant="caption" sx={{ color: partialImage ? '#fff' : theme.palette.text.secondary, mt: 1 }}>
                                  {streamStatus || 'Starting...'}
                                </Typography>
                              </>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={cancelStreaming}
                              sx={{ 
                                fontSize: '0.65rem', 
                                mt: 1,
                                color: partialImage ? '#fff' : theme.palette.text.secondary,
                                borderColor: partialImage ? '#fff' : theme.palette.divider,
                              }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 200,
                            height: 150,
                            borderRadius: 2,
                            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            border: `2px dashed ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: `${theme.palette.primary.main}10`,
                            },
                          }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <UploadIcon sx={{ fontSize: 32, color: theme.palette.text.secondary, mb: 1 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Upload Image
                          </Typography>
                        </Box>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AIIcon />}
                        onClick={handleGenerateAIImage}
                        disabled={generatingAIImage}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        Generate AI Image
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 200,
                        height: 150,
                        borderRadius: 2,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <NoImageIcon sx={{ fontSize: 48, color: theme.palette.text.disabled }} />
                    </Box>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  {editMode ? (
                    <>
                      <TextField
                        fullWidth
                        label="Title"
                        value={editData.title}
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                        sx={{ mb: 2, ...inputStyles }}
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Description"
                        value={editData.description}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                        multiline
                        rows={2}
                        sx={{ ...inputStyles }}
                        size="small"
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="h2" sx={{ mb: 1 }}>
                        {recipe?.title}
                      </Typography>
                      {recipe?.description && (
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                          {recipe.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {recipe?.tags?.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{ backgroundColor: `${theme.palette.primary.main}15` }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              {/* Meta Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Active Time
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      type="number"
                      value={editData.time}
                      onChange={(e) => handleFieldChange('time', e.target.value)}
                      placeholder="minutes"
                      size="small"
                      sx={{ mt: 0.5, ...inputStyles }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {recipe?.time ? `${recipe.time} min` : '-'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Inactive Time
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      type="number"
                      value={editData.inactive_time}
                      onChange={(e) => handleFieldChange('inactive_time', e.target.value)}
                      placeholder="minutes"
                      size="small"
                      sx={{ mt: 0.5, ...inputStyles }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {recipe?.inactive_time ? `${recipe.inactive_time} min` : '-'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Yield
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      value={editData.yield}
                      onChange={(e) => handleFieldChange('yield', e.target.value)}
                      placeholder="e.g., 4 servings"
                      size="small"
                      sx={{ mt: 0.5, ...inputStyles }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {recipe?.yield || '-'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Source
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {recipe?.path ? (
                      <Button
                        size="small"
                        endIcon={<OpenIcon fontSize="small" />}
                        href={recipe.path}
                        target="_blank"
                        sx={{ p: 0, minWidth: 0 }}
                      >
                        View
                      </Button>
                    ) : '-'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Ingredients */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h4">
                  Ingredients
                </Typography>
                {editMode && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddIngredient}
                  >
                    Add
                  </Button>
                )}
              </Box>
              
              {editMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                  {editData.ingredients?.map((ingredient, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Qty"
                        value={ingredient.quantity?.value || ''}
                        onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                        sx={{ width: 70, ...inputStyles }}
                      />
                      <TextField
                        size="small"
                        placeholder="Unit"
                        value={ingredient.quantity?.unit || ''}
                        onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                        sx={{ width: 80, ...inputStyles }}
                      />
                      <TextField
                        size="small"
                        placeholder="Ingredient name"
                        value={ingredient.name || ''}
                        onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                        sx={{ flex: 1, ...inputStyles }}
                      />
                      <TextField
                        size="small"
                        placeholder="Prep (optional)"
                        value={ingredient.preparation || ''}
                        onChange={(e) => handleIngredientChange(index, 'preparation', e.target.value)}
                        sx={{ width: 120, ...inputStyles }}
                      />
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleRemoveIngredient(index)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              ) : (
                <List disablePadding sx={{ mb: 3 }}>
                  {recipe?.ingredients?.map((ingredient, index) => (
                    <ListItem
                      key={index}
                      disablePadding
                      sx={{
                        py: 0.75,
                        borderBottom: index < recipe.ingredients.length - 1 
                          ? `1px solid ${theme.palette.divider}` 
                          : 'none',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box component="span">
                            {ingredient.quantity && (
                              <Box component="span" sx={{ color: theme.palette.primary.main, mr: 1 }}>
                                {ingredient.quantity.value} {ingredient.quantity.unit}
                              </Box>
                            )}
                            {ingredient.name}
                            {ingredient.preparation && (
                              <Box component="span" sx={{ color: theme.palette.text.secondary, ml: 1 }}>
                                ({ingredient.preparation})
                              </Box>
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h4">
                  Instructions
                </Typography>
                {editMode && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddInstruction}
                  >
                    Add
                  </Button>
                )}
              </Box>

              {editMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {editData.instructions?.map((step, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      {/* Step number with reorder buttons */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveInstruction(index, 'up')}
                              disabled={index === 0}
                              sx={{ p: 0.25 }}
                            >
                              <MoveUpIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: `${theme.palette.primary.main}20`,
                            color: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveInstruction(index, 'down')}
                              disabled={index === editData.instructions.length - 1}
                              sx={{ p: 0.25 }}
                            >
                              <MoveDownIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        value={step}
                        onChange={(e) => handleInstructionChange(index, e.target.value)}
                        sx={{ ...inputStyles }}
                      />
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleRemoveInstruction(index)} sx={{ mt: 0.5 }}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              ) : (
                <List disablePadding>
                  {recipe?.instructions?.map((step, index) => (
                    <ListItem key={index} disablePadding sx={{ py: 1, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          backgroundColor: `${theme.palette.primary.main}20`,
                          color: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <ListItemText
                        primary={step}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Metadata
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Recipe ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {recipe?.id}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Owner
                </Typography>
                <Typography variant="body2">
                  {recipe?.user_email || 'Public (System)'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Book
                </Typography>
                <Typography variant="body2">
                  {recipe?.book?.name || 'None'}
                  {recipe?.book?.author && (
                    <Box component="span" sx={{ color: theme.palette.text.secondary }}>
                      {' '}by {recipe.book.author}
                    </Box>
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Created
                </Typography>
                <Typography variant="body2">
                  {recipe?.created_at
                    ? format(new Date(recipe.created_at), 'PPpp')
                    : '-'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipe</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          Are you sure you want to delete "{recipe?.title}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecipeDetail;
